import jwtDecode from 'jwt-decode';
import { CookieNames, LocationSource, TwoWeeks } from './constants';
import cFetch from './lib/cFetch';
import formatLocationData from './lib/formatLocationData';
import formatLocationOnError from './lib/formatLocationOnError';
import getLocationFromCookies from './lib/getLocationFromCookies';
import isServerSide from './lib/isServerSide';
import isValidZipCode from './lib/isValidZipCode';
import LoginCookie from './lib/LoginCookie';
import setCookies from './lib/setCookies';
import toError from './lib/toError';
import type {
  AccountZipCodes,
  ApiConfig,
  CookieClient,
  DecodedToken,
  Fetcher,
  GeolocationApiClient,
  LocationData,
  ResponseData
} from './types';

export default class GeoLocationApi implements GeolocationApiClient {
  private loginCookie: LoginCookie;

  constructor(
    private cookies: CookieClient,
    private internalConfig: ApiConfig,
    private fetcher: Fetcher<ResponseData> = cFetch,
    private fetcherWithAuth: Fetcher<ResponseData> = fetcher
  ) {
    this.loginCookie = new LoginCookie(cookies);
  }

  async getGeoLocation(): Promise<LocationData> {
    const location =
      (await this.maybeGetAccountLocation()) ??
      this.getLocationFromCookies() ??
      (await this.getGeoLocationFromIpAddress());

    this.setLocationCookies(location);

    return location;
  }

  async setGeoLocation(zip: string): Promise<LocationData> {
    /*
     The snippet below was commented out since it was causing issues in
     the purchase process. This functionality will be reintroduced once
     the issues with the purchase process are resolved.
     Reference - REO-1777
    */
    // const CVAccessToken = this.cookies.get(CookieNames.CVAccessToken);
    // const CVIdToken = this.cookies.get(CookieNames.CVIdToken);

    // if (CVAccessToken && CVIdToken) {
    //   await this.updateAccountDeliveryInfo({ CVIdToken, zip });
    // }

    const location =
      (await this.maybeGetAccountLocation()) ??
      (await this.setGeoLocationWithZip(zip));

    this.setLocationCookies(location);

    return location;
  }

  private async getAccountLocation(): Promise<string> {
    try {
      const CVAccessToken = this.cookies.get(CookieNames.CVAccessToken);
      const CVIdToken = this.cookies.get(CookieNames.CVIdToken);
      if (!CVAccessToken || !CVIdToken) return '';

      const userInfo: DecodedToken = jwtDecode(CVIdToken);

      const userProfile = await this.fetcherWithAuth(
        `${this.internalConfig.contactsApi}/${userInfo.user_id}/addresses`
      );

      const zipCodes = userProfile?.content?.reduce<Partial<AccountZipCodes>>(
        (zipcodes, address) => {
          const kind = address?.kind;
          const zip5 = address?.address?.zipCode?.zip5;

          return { ...zipcodes, [kind]: zip5 };
        },
        {}
      );

      return (
        zipCodes?.DeliveryZip ??
        zipCodes?.VerifiedDelivery ??
        zipCodes?.VerifiedHome ??
        ''
      );
    } catch (e) {
      /**
       * instead of handling the error here we will just return
       * and allow the getGeoLocation to proceed as if there was no CVAccessToken
       * */
      return '';
    }
  }

  // if the user logs in, and we haven't already set the MerchHasLoggedIn
  // cookie, then prioritize their account zip over the seo location
  private async maybeGetAccountLocation(): Promise<LocationData | undefined> {
    if (this.loginCookie.shouldRemoveCookie) {
      this.loginCookie.remove();

      return undefined;
    }

    if (!this.loginCookie.shouldSetCookie) {
      return undefined;
    }

    const zip = await this.getAccountLocation();

    if (zip && !isServerSide) {
      this.loginCookie.set();
    }

    return zip ? this.setGeoLocationWithZip(zip) : undefined;
  }

  private getLocationFromCookies(): LocationData | null {
    const location = getLocationFromCookies(
      this.cookies,
      this.internalConfig.ipAddress
    );

    if (!isValidZipCode(location?.zip ?? '')) {
      return null;
    }

    // IP address cookie might not be set, so set it now.
    if (location?.ipAddress) {
      this.cookies.set('CVCurrentIpAddress', location.ipAddress, { path: '/' });
    }

    return location;
  }

  private async getGeoLocationFromIpAddress(): Promise<LocationData> {
    const url = new URL(this.internalConfig.geolocationApi);
    url.searchParams.set('ipaddress', this.internalConfig.ipAddress ?? '');

    return this.getGeoLocationFromApi(url.toString(), LocationSource.Ipaddress);
  }

  private async setGeoLocationWithZip(zip: string): Promise<LocationData> {
    const url = new URL(this.internalConfig.contextApi);
    url.searchParams.set('zip5', zip);

    return this.getGeoLocationFromApi(url.toString(), LocationSource.Zip5);
  }

  private async getGeoLocationFromApi(
    url: string,
    source: LocationSource
  ): Promise<LocationData> {
    try {
      const fetchedLocation = await this.fetcher(url.toString());

      return {
        ...formatLocationData(fetchedLocation),
        ipAddress: this.internalConfig.ipAddress ?? '',
        source
      };
    } catch (err) {
      const location = formatLocationOnError(this.cookies);

      if (this.internalConfig.onError) {
        this.internalConfig.onError(toError(err));
      }

      return {
        ...location,
        ipAddress: location.ipAddress || this.internalConfig.ipAddress || '',
        source: LocationSource.Default
      };
    }
  }

  private setLocationCookies({
    source: _source,
    ...locationWithoutSource
  }: LocationData): void {
    setCookies(this.cookies, locationWithoutSource, {
      path: '/',
      'Max-Age': TwoWeeks
    });
  }

  private async updateAccountDeliveryInfo({
    CVIdToken,
    zip
  }: {
    CVIdToken: string;
    zip: string;
  }): Promise<void> {
    try {
      const userInfo: DecodedToken = jwtDecode(CVIdToken);

      if (userInfo.is_impersonating === 'False')
        await this.fetcherWithAuth(
          `${this.internalConfig.contactsApi}/${userInfo.user_id}/addresses/delivery-zip5`,
          {
            body: JSON.stringify({
              contactId: userInfo.user_id,
              actorId: userInfo.impersonating_user,
              zip5: zip
            }),
            headers: {
              'Content-Type': 'application/json'
            },
            method: 'POST'
          }
        );
    } catch (err) {
      if (this.internalConfig.onError) {
        this.internalConfig.onError(toError(err));
      }
    }
  }
}
